import Link from "next/link";
import { useRef, useEffect, useMemo } from "react";
import { useAtom, useAtomValue } from "jotai";
import LumiSparkle from "@public/lumi-sparkle.svg";
import Image from "next/image";

import {
  tabsApiAtom,
  interestedTopicsAtom,
  selectedStyleAtom,
} from "../constants/budget-atom";
import { useRouter } from "next/router";
import { kBudgetPages } from "../constants/budget-translations";

const BudgetBreadcrumbs: React.FC<{ index: number }> = ({ index }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [tabsApi] = useAtom(tabsApiAtom);
  const interestedTopics = useAtomValue(interestedTopicsAtom);
  const selectedStyle = useAtomValue(selectedStyleAtom);
  const router = useRouter();
  const { locale } = router;
  const pages = useMemo(
    () => kBudgetPages(selectedStyle ? selectedStyle : locale),
    [selectedStyle, locale]
  );

  // Remove the carousel-specific event listener since tabs don't have .on() method
  // The index will be updated via the tabs onValueChange callback instead

  const onItemTapped = (itemIndex: number) => {
    const hasTopics = interestedTopics.length > 0;
    const hasSelectedStyle = selectedStyle !== "";

    // Block navigation to "Interested topics" if no style selected
    if (itemIndex === 1 && !hasSelectedStyle) {
      return;
    }

    // Block navigation to "Your topics" if no style OR no topics
    if (itemIndex === 2 && (!hasSelectedStyle || !hasTopics)) {
      return;
    }

    // Allow navigation using tabs API
    if (tabsApi) {
      // Convert index to tab value

      tabsApi.scrollTo(itemIndex);
    }
  };

  // Auto-scroll to active item
  useEffect(() => {
    if (scrollRef.current && index >= 0) {
      // Scroll to the active element (the one that's highlighted)
      const activeElement = scrollRef.current.querySelector(
        `[data-index="${index + 1}"]`
      );
      if (activeElement) {
        activeElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }, [index]);

  return (
    <div
      ref={scrollRef}
      className="sticky top-0 z-20 mx-8 overflow-x-auto bg-white pt-4 pb-2 md:-mx-2"
      style={{
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
    >
      <div className="flex min-w-max justify-center whitespace-nowrap">
        {pages.map((title, i) => {
          const isFirst = i === 0;
          const isActive = i === index + 1;
          const isLast = i === pages.length - 1;

          return (
            <div
              key={title}
              data-index={i}
              className={`flex flex-shrink-0 items-center ${
                isFirst ? "pl-0" : "pl-2"
              } ${isLast ? "pr-2" : "pr-0"}`}
            >
              {isFirst ? (
                <Link href="/topics/budget_2026" passHref>
                  <span className="text-dark cursor-pointer pt-0.5 font-poppins text-xs hover:text-text-dark">
                    {pages[i]}
                  </span>
                </Link>
              ) : (
                <div
                  className="flex cursor-pointer items-center space-x-2 whitespace-nowrap font-poppins text-xs"
                  onClick={() => onItemTapped(i - 1)}
                >
                  <p>{"<"}</p>
                  <div className="flex flex-wrap space-x-1">
                    {isLast && (
                      <Image
                        src={LumiSparkle}
                        alt="info-icon"
                        height={16}
                        width={16}
                      />
                    )}
                    <p
                      className={`transition-all duration-200 hover:text-text-dark ${
                        isActive ? "text-primary" : "text-dark"
                      }`}
                    >
                      {title}
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BudgetBreadcrumbs;
